export type ExecutionTarget = 'proposal' | 'transaction';
export type ProposalExecutionType =
  | 'account'
  | 'group'
  | 'member'
  | 'policy'
  | 'admin change'
  | 'disaster recovery';

export interface ExecutionRequestResult {
  guardianTransactionId: string;
}

export interface ExecutionCallbacks {
  onSuccess?:
    | ((createdRecordId: string | null) => void | Promise<unknown>)
    | undefined;
  onError?:
    | ((errorMessage: string | null) => void | Promise<unknown>)
    | undefined;
  onComplete?: (() => void | Promise<unknown>) | undefined;
}

export interface ExecutionEntry {
  id: string;
  executionTarget: ExecutionTarget;
  executionTargetId: string | null;
  proposalType: ProposalExecutionType | null;
  guardianTransactionId: string | null;
  immediateError: string | null;
  createdAt: number;
  completedAt: number | null;
  callbacks: ExecutionCallbacks;
}

type ExecutionAction =
  | { type: 'add'; entry: ExecutionEntry }
  | { type: 'setGuardianTransactionId'; id: string; value: string }
  | {
      type: 'setImmediateError';
      id: string;
      value: string;
      completedAt: number;
    }
  | { type: 'markCompleted'; id: string; completedAt: number }
  | { type: 'remove'; id: string };

/**
 * Reports whether a specific proposal or transaction still has a locally
 * tracked execution in flight. The originating screen uses this identity to
 * close the gap before its next server poll exposes the processing state.
 */
export function hasActiveExecution(
  state: ExecutionEntry[],
  executionTarget: ExecutionTarget,
  executionTargetId: string
): boolean {
  return state.some(
    (entry) =>
      entry.executionTarget === executionTarget &&
      entry.executionTargetId === executionTargetId &&
      entry.completedAt === null
  );
}

/**
 * Applies lifecycle transitions to the independent execution entries shown in
 * the tray. Every transition is immutable so concurrent cards cannot overwrite
 * one another.
 */
export function executionReducer(
  state: ExecutionEntry[],
  action: ExecutionAction
): ExecutionEntry[] {
  switch (action.type) {
    case 'add':
      // Preserve start order when multiple executions run concurrently.
      return [...state, action.entry];
    case 'setGuardianTransactionId':
      // Attach the server identity after the asynchronous execute call returns.
      return state.map((entry) =>
        entry.id === action.id
          ? { ...entry, guardianTransactionId: action.value }
          : entry
      );
    case 'setImmediateError':
      // A failed execute request never receives a GuardianTransaction to poll.
      return state.map((entry) =>
        entry.id === action.id
          ? {
              ...entry,
              immediateError: action.value,
              completedAt: action.completedAt,
            }
          : entry
      );
    case 'markCompleted':
      // Keep the first completion time stable across repeated status renders.
      return state.map((entry) =>
        entry.id === action.id && entry.completedAt === null
          ? { ...entry, completedAt: action.completedAt }
          : entry
      );
    case 'remove':
      // Removal is shared by manual dismissal and successful auto-dismissal.
      return state.filter((entry) => entry.id !== action.id);
    default: {
      // Adding a new action requires an explicit transition above.
      const exhaustiveAction: never = action;
      throw new Error(
        `Unsupported execution action: ${JSON.stringify(exhaustiveAction)}`
      );
    }
  }
}
